import * as React from 'react';

import { useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';
import { useNavigate } from 'react-router-dom';

import { PrimitiveForm } from 'components';
import { useAttributeValue } from 'context';
import { createAttributeConfig, updateAttributeConfig } from 'services';
import { PATHNAME } from 'utils';

import { AttributeFormAction } from './action';
import { AttributeFormContent } from './content';

const {
  LOCAL: {
    ATTRIBUTES: { BASE_URL: ATTRIBUTES_BASE_URL },
  },
} = PATHNAME;

type FieldType = Pick<
  ReturnType<typeof useAttributeValue>,
  'name' | 'type' | 'helpText' | 'isFilter'
>;

const AttributeForm = () => {
  const [form] = Form.useForm<FieldType>();
  const { id = '', categories = [], defaultName, mode } = useAttributeValue();
  const navigate = useNavigate();

  const onFinish = useLockFn(
    async ({ name = '', type, helpText = '', isFilter = false }: FieldType) => {
      const isUpdatedMode = mode === 'Update';

      const params: Parameters<typeof updateAttributeConfig>[0] = {
        id,
      };
      const body: Parameters<typeof updateAttributeConfig>[1] = {
        name,
        type: type!,
        helpText,
        isFilter,
        categories: categories.map(({ id, values }) => ({
          id,
          values,
        })),
      };

      (isUpdatedMode
        ? updateAttributeConfig(params, body)
        : createAttributeConfig(body)
      )
        .then(() => {
          notification.success({
            message: `فیلد ${isUpdatedMode ? defaultName : name} با موفقیت ${isUpdatedMode ? 'ویرایش' : 'ساخته'} شد.`,
          });
          navigate(ATTRIBUTES_BASE_URL);
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'categories',
          value: categories.map(({ id, title }) => ({
            key: id,
            label: title,
            value: id,
          })),
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='last:*:rounded last:*:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:basis-[70%] xl:last:*:basis-[30%]'
        gap='large'
      >
        <AttributeFormContent />
        <AttributeFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { AttributeForm };
